import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Command as Cmdk } from "cmdk";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useListTree, useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import type { LibraryNode } from "../../../lib/types";
import { useCommandPalette } from "./CommandPaletteContext";
import { useCommandIndex } from "./use-command-index";
import { loadRecency, recordCommandUse, selectRecentCommands } from "./commands/recent";
import { KIND_COLOR, KIND_LABEL, type Command, type CommandContext, type CreateKind, type McpPrompt } from "./types";
import { CheatSheet } from "./CheatSheet";
import { NewFolderModal } from "../scope/NewFolderModal";
import { NewTaskModal } from "../scope/NewTaskModal";
import { NewRoutineModal } from "../scope/NewRoutineModal";
import { NewPageModal } from "../page/NewPageModal";
import { NewTableModal } from "../table/NewTableModal";
import { PromptArgsModal } from "../scope/PromptArgsModal";
import { PromptDisplayModal } from "../scope/PromptDisplayModal";
import { CopyMcpEndpointModal } from "../endpoints/CopyMcpEndpointModal";
import { humanizePromptName } from "./format";

/**
 * Sprint 3 — the Cmd-K command palette. Mounted once in AppShell; opened via the
 * global Cmd+K handler or the TopBar hint. Composes navigation / create / prompt
 * / action / system / recent commands from the live TanStack caches, ranks them
 * (fuzzy × recency × kind), and renders a single keyboard-first list on cmdk.
 *
 * Lazy on open: the inner index (and its member/prompt queries) only mounts once
 * the palette has been opened, so the rest of the app pays nothing for it.
 */
export function CommandPalette() {
  const { open } = useCommandPalette();
  // Latch: mount the (query-firing) index only once the palette is first opened,
  // then keep it mounted so caches stay warm. Render-phase derived state (the
  // guarded pattern) avoids an open→setState effect.
  const [everOpened, setEverOpened] = useState(open);
  if (open && !everOpened) setEverOpened(true);
  if (!everOpened) return null;
  return <CommandPaletteInner />;
}

interface RouteContext {
  currentNodeId: string | null;
  currentNodeType: string | null;
  currentNodeName: string | null;
}

function resolveRouteContext(
  pathname: string,
  treeNodes: LibraryNode[],
  rootId: string | null,
): RouteContext {
  // /agents/:id  or  /agents/:id/tables/:tableId — the :id is the container.
  const id = pathname.match(/^\/agents\/([^/]+)/)?.[1];
  if (id) {
    const node = treeNodes.find((n) => n.id === id);
    if (node && (node.type === "agent" || node.type === "folder")) {
      return { currentNodeId: id, currentNodeType: node.type, currentNodeName: node.name };
    }
    // Assume an agent we haven't cached yet — still a valid container.
    return { currentNodeId: id, currentNodeType: "agent", currentNodeName: node?.name ?? null };
  }
  // Anything else (/library/:id, dashboards, settings) has no create container;
  // fall back to the workspace root agent so creates still have a parent.
  return { currentNodeId: rootId, currentNodeType: null, currentNodeName: null };
}

function CommandPaletteInner() {
  const { open, mode, setOpen, setMode, openCheatsheet } = useCommandPalette();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspaceId } = useAuth();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const wsInfo = useWorkspaceInfo();

  const [query, setQuery] = useState("");
  const [createKind, setCreateKind] = useState<CreateKind | null>(null);
  const [argsPrompt, setArgsPrompt] = useState<McpPrompt | null>(null);
  const [displayPrompt, setDisplayPrompt] = useState<{ prompt: McpPrompt; args: Record<string, string> } | null>(null);
  const [copyEndpointOpen, setCopyEndpointOpen] = useState(false);

  const rootNodeId = wsInfo.data?.root_node_id ?? null;
  const allNodes = useMemo(() => tree.data ?? [], [tree.data]);

  const routeCtx = useMemo(
    () => resolveRouteContext(location.pathname, allNodes, rootNodeId),
    [location.pathname, allNodes, rootNodeId],
  );

  // Reset the query whenever the palette opens — render-phase derived-state
  // (adjust state on `open` transition, no effect needed).
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setQuery("");
  }

  const ctx: CommandContext = useMemo(
    () => ({
      currentNodeId: routeCtx.currentNodeId,
      currentNodeType: routeCtx.currentNodeType,
      currentNodeName: routeCtx.currentNodeName,
      rootNodeId,
      navigate: (to: string) => navigate(to),
      openCreate: (kind: CreateKind) => setCreateKind(kind),
      runPrompt: (prompt: McpPrompt) => {
        const needsArgs = (prompt.arguments ?? []).some((a) => a.required);
        if (needsArgs) setArgsPrompt(prompt);
        else setDisplayPrompt({ prompt, args: {} });
      },
      openCheatsheet: () => setMode("cheatsheet"),
      openCopyEndpoint: () => setCopyEndpointOpen(true),
      close: () => setOpen(false),
    }),
    [routeCtx, rootNodeId, navigate, setMode, setOpen],
  );

  const { all, results } = useCommandIndex(query, ctx);

  // First-time banner — shown once per workspace until dismissed (S3.22). The
  // seen flag is read once (lazy init); no effect.
  const seenKey = currentWorkspaceId ? `kuvio_command_palette_cheatsheet_seen_${currentWorkspaceId}` : null;
  const [seen, setSeen] = useState(() => {
    if (!seenKey) return true;
    try {
      return localStorage.getItem(seenKey) === "1";
    } catch {
      return true;
    }
  });
  const showBanner = open && !seen;
  function dismissBanner() {
    setSeen(true);
    try {
      if (seenKey) localStorage.setItem(seenKey, "1");
    } catch {
      /* ignore */
    }
  }

  function runCommand(cmd: Command) {
    recordCommandUse(currentWorkspaceId, cmd.id);
    if (showBanner) dismissBanner();
    cmd.execute();
    // Cheatsheet stays open (it flips mode); everything else dismisses the
    // palette. Create / prompt commands set their downstream modal first.
    if (cmd.id !== "system:shortcuts") setOpen(false);
  }

  const recents = useMemo(
    () => selectRecentCommands(all, loadRecency(currentWorkspaceId), 5),
    [all, currentWorkspaceId],
  );

  function pickById(id: string): Command | undefined {
    return all.find((c) => c.id === id);
  }
  const workspaceSurfaces = ["/workforce", "/tables", "/library", "/connections", "/my-tasks", "/activity"]
    .map((p) => pickById(`action:goto:${p}`))
    .filter(Boolean) as Command[];
  const quickCreate = ["page", "task", "folder", "agent"]
    .map((k) => pickById(`create:${k}`))
    .filter(Boolean) as Command[];
  const topPrompts = all.filter((c) => c.kind === "prompt").slice(0, 3);

  // Close the create/prompt downstream modal handlers.
  function closeCreate() {
    setCreateKind(null);
  }

  return (
    <>
      <style>{paletteCss}</style>

      {open && (
        <div
          style={overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            style={paletteBox}
            role="dialog"
            aria-label="Command palette"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
              }
            }}
          >
            <Cmdk shouldFilter={false} loop label="Command palette">
              {showBanner && (
                <div style={bannerStyle}>
                  <i className="ti ti-bulb" style={{ fontSize: 14 }} aria-hidden="true" />
                  <span style={{ flex: 1 }}>
                    Press <kbd style={kbd}>?</kbd> to see all commands
                  </span>
                  <button onClick={dismissBanner} style={bannerDismiss} aria-label="Dismiss">
                    <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden="true" />
                  </button>
                </div>
              )}

              <div style={inputRow}>
                <i
                  className="ti ti-search"
                  style={{ fontSize: 16, color: "var(--color-text-tertiary)", flexShrink: 0 }}
                  aria-hidden="true"
                />
                <Cmdk.Input
                  value={query}
                  onValueChange={(v) => {
                    setQuery(v);
                    // Typing "?" on an empty query jumps to the cheatsheet (S3.20).
                    if (v === "?") {
                      setQuery("");
                      setMode("cheatsheet");
                    } else if (mode === "cheatsheet") {
                      setMode("default");
                    }
                  }}
                  autoFocus
                  placeholder={mode === "cheatsheet" ? "Search all commands…" : "Search or run a command…"}
                  className="kuvio-cmdk-input"
                />
                {mode === "cheatsheet" && (
                  <button onClick={() => setMode("default")} style={escHint} title="Back">
                    Back
                  </button>
                )}
              </div>

              <Cmdk.List style={listStyle}>
                {mode === "cheatsheet" ? (
                  <CheatSheet all={all} onRun={runCommand} />
                ) : query.trim() ? (
                  <>
                    <Cmdk.Empty style={emptyStyle}>No commands match “{query}”.</Cmdk.Empty>
                    {results.map((cmd) => (
                      <CommandRow key={cmd.id} cmd={cmd} value={cmd.id} onRun={runCommand} />
                    ))}
                  </>
                ) : (
                  <EmptyStateSections
                    recents={recents}
                    surfaces={workspaceSurfaces}
                    create={quickCreate}
                    prompts={topPrompts}
                    onRun={runCommand}
                    onShowAll={openCheatsheet}
                  />
                )}
              </Cmdk.List>

              <div style={footer}>
                <span><kbd style={kbd}>↑</kbd><kbd style={kbd}>↓</kbd> navigate</span>
                <span><kbd style={kbd}>↵</kbd> execute</span>
                <span><kbd style={kbd}>esc</kbd> close</span>
                <span><kbd style={kbd}>?</kbd> shortcuts</span>
              </div>
            </Cmdk>
          </div>
        </div>
      )}

      {/* Downstream modals — the palette is the only opener. Parent / owner
          default to the route's container node (or the workspace root). */}
      <CreateModalHost
        kind={createKind}
        routeCtx={routeCtx}
        rootNodeId={rootNodeId}
        allNodes={allNodes}
        navigate={navigate}
        onClose={closeCreate}
      />

      <PromptArgsModal
        open={argsPrompt != null}
        prompt={argsPrompt}
        onClose={() => setArgsPrompt(null)}
        onSubmit={(args) => {
          const prompt = argsPrompt;
          setArgsPrompt(null);
          if (prompt) setDisplayPrompt({ prompt, args });
        }}
      />

      <PromptDisplayModal
        open={displayPrompt != null}
        onClose={() => setDisplayPrompt(null)}
        title={displayPrompt ? humanizePromptName(displayPrompt.prompt.name) : ""}
        subtitle={displayPrompt?.prompt.description}
        promptName={displayPrompt?.prompt.name ?? ""}
        args={displayPrompt?.args ?? {}}
      />

      <CopyMcpEndpointModal
        open={copyEndpointOpen}
        onClose={() => setCopyEndpointOpen(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function CommandRow({ cmd, value, onRun }: { cmd: Command; value: string; onRun: (c: Command) => void }) {
  return (
    <Cmdk.Item value={value} onSelect={() => onRun(cmd)} className="kuvio-cmdk-item">
      <i
        className={`ti ti-${cmd.icon ?? "circle"}`}
        style={{ fontSize: 16, color: KIND_COLOR[cmd.kind], flexShrink: 0, width: 18, textAlign: "center" }}
        aria-hidden="true"
      />
      <span style={rowLabel}>{cmd.label}</span>
      {cmd.subtitle ? <span style={rowSubtitle}>{cmd.subtitle}</span> : null}
      <span style={rowKind}>{KIND_LABEL[cmd.kind]}</span>
    </Cmdk.Item>
  );
}

function EmptyStateSections({
  recents,
  surfaces,
  create,
  prompts,
  onRun,
  onShowAll,
}: {
  recents: Command[];
  surfaces: Command[];
  create: Command[];
  prompts: Command[];
  onRun: (c: Command) => void;
  onShowAll: () => void;
}) {
  return (
    <>
      {recents.length > 0 && (
        <Cmdk.Group heading="Recent" className="kuvio-cmdk-group">
          {recents.map((cmd) => (
            <CommandRow key={`recent:${cmd.id}`} cmd={cmd} value={`recent:${cmd.id}`} onRun={onRun} />
          ))}
        </Cmdk.Group>
      )}
      {surfaces.length > 0 && (
        <Cmdk.Group heading="Workspace" className="kuvio-cmdk-group">
          {surfaces.map((cmd) => (
            <CommandRow key={`ws:${cmd.id}`} cmd={cmd} value={`ws:${cmd.id}`} onRun={onRun} />
          ))}
        </Cmdk.Group>
      )}
      {create.length > 0 && (
        <Cmdk.Group heading="Create" className="kuvio-cmdk-group">
          {create.map((cmd) => (
            <CommandRow key={`new:${cmd.id}`} cmd={cmd} value={`new:${cmd.id}`} onRun={onRun} />
          ))}
        </Cmdk.Group>
      )}
      {prompts.length > 0 && (
        <Cmdk.Group heading="Run prompt" className="kuvio-cmdk-group">
          {prompts.map((cmd) => (
            <CommandRow key={`prm:${cmd.id}`} cmd={cmd} value={`prm:${cmd.id}`} onRun={onRun} />
          ))}
        </Cmdk.Group>
      )}
      <Cmdk.Group heading="More" className="kuvio-cmdk-group">
        <Cmdk.Item value="more:cheatsheet" onSelect={onShowAll} className="kuvio-cmdk-item">
          <i
            className="ti ti-keyboard"
            style={{ fontSize: 16, color: KIND_COLOR.system, flexShrink: 0, width: 18, textAlign: "center" }}
            aria-hidden="true"
          />
          <span style={rowLabel}>Show all commands</span>
          <span style={rowSubtitle}>Press ? anytime</span>
          <span style={rowKind}>{KIND_LABEL.system}</span>
        </Cmdk.Item>
      </Cmdk.Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// Create-modal host — picks the right modal + resolves parent/owner.
// ---------------------------------------------------------------------------

function CreateModalHost({
  kind,
  routeCtx,
  rootNodeId,
  navigate,
  onClose,
}: {
  kind: CreateKind | null;
  routeCtx: RouteContext;
  rootNodeId: string | null;
  allNodes: LibraryNode[];
  navigate: (to: string) => void;
  onClose: () => void;
}) {
  // A container is an agent/folder we're viewing; everything else → root.
  const isContainer = routeCtx.currentNodeType === "agent" || routeCtx.currentNodeType === "folder";
  const containerId = isContainer ? routeCtx.currentNodeId : rootNodeId;
  // Tasks + tables must be hosted on an agent specifically.
  const ownerAgentId = routeCtx.currentNodeType === "agent" ? routeCtx.currentNodeId : rootNodeId;

  // Sprint 23.5 — "agent" kind opens the Hire wizard (conversational
  // composition) instead of the old NewAgentModal. The kind→modal seam is
  // preserved for folder/task/routine/page/table; agent is now a route.
  useEffect(() => {
    if (kind === "agent") {
      onClose();
      navigate("/hire");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  return (
    <>
      <NewFolderModal
        open={kind === "folder"}
        onClose={onClose}
        parentId={containerId ?? ""}
        parentName={routeCtx.currentNodeName ?? undefined}
      />
      <NewTaskModal open={kind === "task"} onClose={onClose} nodeId={ownerAgentId ?? ""} />
      <NewRoutineModal open={kind === "routine"} onClose={onClose} agentId={ownerAgentId ?? ""} />
      <NewPageModal
        open={kind === "page"}
        onClose={onClose}
        parentId={containerId ?? ""}
        onCreated={(node) => {
          onClose();
          navigate(`/library/${node.id}`);
        }}
      />
      <NewTableModal
        open={kind === "table"}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        ownerAgentId={ownerAgentId ?? ""}
        onCreated={(tableNodeId) => {
          onClose();
          if (ownerAgentId) navigate(`/agents/${ownerAgentId}/tables/${tableNodeId}`);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: "12vh",
  zIndex: 300,
  fontFamily: "var(--font-sans)",
};

const paletteBox: CSSProperties = {
  width: 640,
  maxWidth: "92vw",
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const inputRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 16px",
  borderBottom: "var(--kuvio-border-default)",
};

const listStyle: CSSProperties = {
  maxHeight: "52vh",
  overflowY: "auto",
  padding: 6,
};

const rowLabel: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const rowSubtitle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: 1,
  minWidth: 0,
};

const rowKind: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  flexShrink: 0,
  marginLeft: "auto",
};

const footer: CSSProperties = {
  display: "flex",
  gap: 14,
  alignItems: "center",
  padding: "8px 16px",
  borderTop: "var(--kuvio-border-default)",
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  background: "var(--color-background-secondary)",
};

const kbd: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 16,
  height: 16,
  padding: "0 4px",
  margin: "0 2px",
  border: "var(--kuvio-border-default)",
  borderRadius: 4,
  background: "var(--color-background-primary)",
  fontSize: 10,
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  color: "var(--color-text-secondary)",
};

const emptyStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  padding: "20px 12px",
  textAlign: "center",
};

const bannerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 16px",
  background: "var(--c-amber-50, var(--color-background-secondary))",
  color: "var(--color-text-secondary)",
  fontSize: 12,
  borderBottom: "var(--kuvio-border-default)",
};

const bannerDismiss: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  padding: 2,
  display: "inline-flex",
};

const escHint: CSSProperties = {
  border: "var(--kuvio-border-default)",
  background: "transparent",
  borderRadius: 6,
  fontSize: 11,
  padding: "2px 8px",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  flexShrink: 0,
};

/** cmdk is unstyled — these rules cover the input, items, selected state, and
 *  group headings using kuvio tokens. */
const paletteCss = `
.kuvio-cmdk-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  font-family: var(--font-sans);
  color: var(--color-text-primary);
}
.kuvio-cmdk-input::placeholder { color: var(--color-text-tertiary); }
.kuvio-cmdk-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  user-select: none;
}
.kuvio-cmdk-item[data-selected="true"] { background: var(--color-background-secondary); }
.kuvio-cmdk-item[data-disabled="true"] { opacity: 0.5; cursor: default; }
.kuvio-cmdk-group { padding-top: 4px; }
[cmdk-group-heading] {
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-tertiary);
  padding: 8px 10px 4px;
}
`;
